import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { OffersPublicService, ProductCreateService, ProductDeleteService, ProductUpdateService, ProductsAllService, OffersAllService, OffersCreateService, OffersAssociateProductsService, OffersDeleteService, OffersByIdService, OffersUpdateService, OffersPublicOnlyService } from "./OffersService"
import useAuthContext from "../../hooks/useAuthContext"
import { useToast } from "@chakra-ui/toast"

const queryOffers = () => {
  const queryClient = useQueryClient();
  const { user } = useAuthContext();
  const token = user?.access_token;
  const toast = useToast();

  const offerPublic = () => {
    const { data, isLoading, isError, error } = useQuery(
      ['offersPublic'],
      () => OffersPublicService(),
      {
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        retry: false,
        staleTime: 1000 * 60 * 5, // 5 minutes
      }
    )

    const dataResponse = data?.data.data

    return { data: dataResponse, isLoading, isError, error }
  }
  
  const offerPublicOnly = () => {
    const { data, isLoading, isError, error } = useQuery(
      ['offersPublicOnly'],
      () => OffersPublicOnlyService(),
      {
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        retry: false,
        staleTime: 1000 * 60 * 5, // 5 minutes
      }
    )

    const dataResponse = data?.data.data

    return { data: dataResponse, isLoading, isError, error }
  }

  const offersAll = (search = '') => {
    const { data, isLoading, isError, error } = useQuery(
      ['offersAll', search],
      () => OffersAllService({ token, search, orderBy: 'desc', orderField: 'date_init' }),
      {
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        retry: false,
        staleTime: 1000 * 60 * 5, // 5 minutes
      }
    )

    const dataResponse = data?.data.data.data || []

    return { data: dataResponse, isLoading, isError, error }
  }

  const offersById = (id) => {
    const { data, isLoading, isError, error, refetch } = useQuery(
      ['offerById'],
      () =>  OffersByIdService({ token, id }),
      {
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        retry: false,
        staleTime: 1000 * 60 * 5, // 5 minutes
      }
    )

    const dataResponse = data?.data.data || []

    return { data: dataResponse, isLoading, isError, error, refetch }
  }

  const offerCreate = () => {
    const { isLoading, isError, mutate, isSuccess, data } = useMutation((opt) => OffersCreateService({
      data: opt,
      token,
    }))

    return { isLoading, isError, mutate, isSuccess, data: data?.data.data }
  };

  const offerUpdate = () => {
    const { isLoading, isError, mutate, isSuccess, data } = useMutation((opt) => OffersUpdateService({
      data: opt,
      token,
    }))

    return { isLoading, isError, mutate, isSuccess, data: data?.data.data }
  };

  const offerAssociateProducts = () => {
    const { isLoading, isError, mutate, isSuccess } = useMutation((opt) => OffersAssociateProductsService({
      data: opt,
      token,
    }))

    return { isLoading, isError, mutate, isSuccess }
  };

  const offerDeleteProducts = () => {
    const { isLoading, isError, mutate, isSuccess } = useMutation((opt) => OffersDeleteService({
      ids: opt.ids,
      token,
    }), {
      onSuccess: (_, opt) => {
        queryClient.invalidateQueries(['offersAll', '']);
        queryClient.invalidateQueries(['offersAll', opt.search || '']);
        toast({
          title: 'Promoción eliminada',
          status: 'success',
          duration: 2000,
          isClosable: true,
          position: 'top-right',
        });
      },
      onError: () => {
        toast({
          title: 'Error al eliminar la promoción',
          status: 'error',
          duration: 2000,
          isClosable: true,
          position: 'top-right',
        });
      }
    })

    return { isLoading, isError, mutate, isSuccess }
  };

  const productsAll = (search = '', promo_id) => {
    const { data, isLoading, isError, error } = useQuery(
      ['productsAll', search, promo_id],
      () => ProductsAllService(search, undefined, undefined, promo_id),
      {
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        retry: false,
        staleTime: 1000 * 60 * 5, // 5 minutes
      }
    )

    const dataResponse = data?.data.data

    return { data: dataResponse, isLoading, isError, error }
  }

  const productCreate = () => {
    const { isLoading, isError, mutate, isSuccess } = useMutation((opt) => ProductCreateService({
      product: opt,
      token,
    }), {
      onSuccess: (data, opt) => {
        toast({
          title: 'Producto agregado',
          status: 'success',
          duration: 2000,
          isClosable: true,
          position: 'top-right',
        });

        // Actualizar caché con el search actual
        const searchKey = opt.search || '';
        const oldCache = queryClient.getQueryData(['productsAll', searchKey, undefined]);
        if (oldCache) {
          const dataResponse = [...oldCache.data.data, data.data.data];
          const newCache = {
            ...oldCache,
            data: {
              ...oldCache.data,
              data: dataResponse,
            }
          };
          queryClient.setQueryData(['productsAll', searchKey, undefined], newCache);
        }

        // Invalidar todas las queries de productsAll
        queryClient.invalidateQueries({ queryKey: ['productsAll'] });
      }
    })

    return { isLoading, isError, mutate, isSuccess }
  }


  const productUpdate = () => {
    const { isLoading, isError, mutate, isSuccess } = useMutation((opt) => ProductUpdateService({
      product: opt,
      token,
    }), {
      onSuccess: (data, opt) => {
        if (opt.hasNotification) {
          toast({
            title: 'Producto actualizado',
            status: 'success',
            duration: 2000,
            isClosable: true,
            position: 'top-right',
          });
        }

        const searchKey = opt.search || '';
        const oldCache = queryClient.getQueryData(['productsAll', searchKey, undefined]);
        if (oldCache) {
          const dataResponse = oldCache.data.data.map((item) => {
            if (item.id === data.data.data.id) {
              return data.data.data;
            }
            return item;
          });
          const newCache = {
            ...oldCache,
            data: {
              ...oldCache.data,
              data: dataResponse,
            }
          };
          queryClient.setQueryData(['productsAll', searchKey, undefined], newCache);
        }

        // Invalidar todas las queries de productsAll
        queryClient.invalidateQueries({ queryKey: ['productsAll'] });
      }
    })

    return { isLoading, isError, mutate, isSuccess }
  };

  const productDelete = () => {
    const { isLoading, isError, mutate, isSuccess } = useMutation((opt) => ProductDeleteService({
      id: opt.id,
      token,
    }), {
      onSuccess: (data, opt) => {
        toast({
          title: 'Producto eliminado',
          status: 'success',
          duration: 2000,
          isClosable: true,
          position: 'top-right',
        });

        const searchKey = opt.search || '';
        const oldCache = queryClient.getQueryData(['productsAll', searchKey, undefined]);
        if (oldCache) {
          const dataResponse = oldCache.data.data.filter((item) => item.id !== opt.id);
          const newCache = {
            ...oldCache,
            data: {
              ...oldCache.data,
              data: dataResponse,
            }
          };
          queryClient.setQueryData(['productsAll', searchKey, undefined], newCache);
        }

        // Invalidar todas las queries de productsAll
        queryClient.invalidateQueries({ queryKey: ['productsAll'] });
      }
    })

    return { isLoading, isError, mutate, isSuccess }
  }

  return {
    offerPublic,
    offerPublicOnly,
    offersAll,
    offersById,
    offerCreate,
    offerUpdate,
    offerAssociateProducts,
    offerDeleteProducts,
    productsAll,
    productCreate,
    productUpdate,
    productDelete
  }
}

export default queryOffers